// Driver details → "Device" tab (wave 3, W2_Portal_BookingDetails look): the
// app build the driver runs and every device grant a booking needs, with
// what to tell them on the phone when one is missing. Values come from the
// /users/{uid} record the details page hands in (appVersion / appBuild /
// appPlatform / appLastSeenAt from AppCommon; perm* from the driver home);
// nothing is read here. Times are Addis Ababa, 12-hour.
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { tokens as c } from '../theme';
import { formatAddisDateTime } from '../common/exportUtils';
import { DetailCard, InfoRow, StatusChip, IconCircle, HintIcon } from '../components/modern';
import { PERM_ICONS, PERM_STATES, permissionStatus, lockedPhoneReady } from '../components/driverPermissions';

// One tone per grant state (icon + word, never colour alone).
const STATE_TONE = { ok: 'success', warn: 'amber', bad: 'error', unknown: 'neutral' };
const STATE_HUE = { ok: 'success', warn: 'amber', bad: 'danger', unknown: 'neutral' };
const STATE_ICON = { ok: CheckRoundedIcon, warn: WarningAmberRoundedIcon, bad: CloseRoundedIcon, unknown: HelpOutlineRoundedIcon };

export default function DriverDeviceDetails({ data }) {
  const { t, i18n } = useTranslation();
  const L = (key, dflt) => t(key, { defaultValue: dflt });
  const d = data || {};
  const perms = permissionStatus(d, L);
  const reported = !!d.permCheckedAt;
  const ready = reported && lockedPhoneReady(d, L);
  const stateLabel = {
    ok: L('perm_state_ok', 'OK'),
    warn: L('perm_state_warn', 'Partial'),
    bad: L('perm_state_bad', 'Missing'),
    unknown: L('perm_state_unknown', 'Not reported'),
  };
  const when = (ms) => (ms ? formatAddisDateTime(ms) : null);

  const readiness = reported ? (
    <StatusChip
      tone={ready ? 'success' : 'error'}
      icon={ready ? <CheckRoundedIcon /> : <WarningAmberRoundedIcon />}
      label={ready ? L('locked_phone_ready', 'Bookings reach a locked phone') : L('locked_phone_not_ready', 'Bookings may be missed on a locked phone')}
      sx={{ '& .MuiChip-label': { px: 1, whiteSpace: 'normal' }, height: 'auto', minHeight: 26, py: 0.25 }}
    />
  ) : (
    <StatusChip tone="neutral" icon={<HelpOutlineRoundedIcon />} label={L('perm_unknown_hint', 'Not reported yet (older app build)')} />
  );

  return (
    <Box dir={i18n.dir()} sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 3fr)' }, alignItems: 'start' }}>
      <DetailCard icon={<PhoneAndroidOutlinedIcon />} hue="info" title={L('device_app', 'App on the phone')}>
        <InfoRow label={L('app_version', 'App version')}>
          {d.appVersion ? (
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {`${d.appVersion}${d.appBuild ? ` (${d.appBuild})` : ''}`}
            </Box>
          ) : null}
        </InfoRow>
        <InfoRow label={L('platform', 'Platform')}>{d.appPlatform ? (d.appPlatform === 'ios' ? 'iOS' : 'Android') : d.userPlatform || null}</InfoRow>
        <InfoRow label={L('last_seen', 'Last seen')}>{when(d.appLastSeenAt)}</InfoRow>
        <InfoRow label={L('permissions_checked', 'Permissions checked')}>{when(d.permCheckedAt)}</InfoRow>
      </DetailCard>

      <DetailCard
        icon={<VerifiedUserOutlinedIcon />}
        hue="accent"
        title={L('device_permissions', 'Permissions')}
        action={
          <HintIcon
            id="device-permissions-note"
            hint={L(
              'device_permissions_note',
              'Reported by the driver app when a value changes and at least weekly. "Display over other apps" is what lets a new booking appear on a locked phone; "Always" precise location is what dispatch needs.'
            )}
          />
        }
        list={false}
      >
        <Box sx={{ mb: 1.5 }}>{readiness}</Box>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-describedby="device-permissions-note">
          {perms.map((p) => {
            const Icon = PERM_ICONS[p.key];
            const StateIcon = STATE_ICON[p.state] || HelpOutlineRoundedIcon;
            const showFix = p.state === PERM_STATES.bad || p.state === PERM_STATES.warn;
            return (
              <Box
                component="li"
                key={p.key}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderTop: `1px solid ${c.OUTLINE}`, '&:first-of-type': { borderTop: 0, pt: 0.5 } }}
              >
                <IconCircle icon={<Icon />} hue={STATE_HUE[p.state] || 'neutral'} size={34} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span" sx={{ fontWeight: 700, fontSize: 14.5, color: c.INK }}>
                      {p.label}
                    </Typography>
                    <StatusChip tone={STATE_TONE[p.state] || 'neutral'} icon={<StateIcon />} label={stateLabel[p.state]} />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: c.INK_2, mt: 0.25, overflowWrap: 'anywhere' }}>{p.text}</Typography>
                  {showFix ? (
                    <Typography sx={{ fontSize: 13.5, color: c.AMBER_TEXT, mt: 0.5, overflowWrap: 'anywhere' }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {L('perm_fix_label', 'Tell the driver')}:
                      </Box>{' '}
                      {p.fix}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DetailCard>
    </Box>
  );
}
